import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { api, type Response } from '@/lib/api'
import type { SelectOption } from '@/components/SearchableSelect'

/**
 * The lookup lists behind every dropdown on the site.
 *
 * The old JS re-fetched these on each page load and rebuilt the select2 widget
 * each time. They are cached here instead — the contents change about as often
 * as the org chart, and the same three lists back Home, the vacancy listing and
 * the apply forms.
 */

export interface MstCareer {
  mstCareerID: number
  CareerName: string | null
}
export interface MstCompany {
  mstCompanyID: number
  CompanyName: string | null
}
export interface MstPosition {
  mstPositionID: number
  PositionName: string | null
}

const LOOKUP_STALE_TIME = 10 * 60 * 1000

function useLookup<T>(key: string, path: string): UseQueryResult<Response<T[]>> {
  return useQuery({
    queryKey: ['masterData', key],
    queryFn: () => api.get<Response<T[]>>(path),
    staleTime: LOOKUP_STALE_TIME,
  })
}

export const useCareers = () => useLookup<MstCareer>('career', '/api/ecareer/career/list')

/**
 * `company/listCompanyCandidate`, not `company/list`.
 *
 * home.js and vacancy.js both use the *Candidate variants: those return only the
 * companies and positions that actually have vacancies, so the filter never
 * offers a value that yields an empty result.
 */
export const useCompanies = () =>
  useLookup<MstCompany>('company-candidate', '/api/ecareer/company/listCompanyCandidate')

export const usePositions = () =>
  useLookup<MstPosition>('position-candidate', '/api/ecareer/position/listPositionCandidate')

/* --------------------------- option mappers --------------------------- */

export function careerOptions(data: MstCareer[] | null | undefined): SelectOption[] {
  return (data ?? []).map((item) => ({
    value: String(item.mstCareerID),
    label: item.CareerName ?? '',
  }))
}

export function companyOptions(data: MstCompany[] | null | undefined): SelectOption[] {
  return (data ?? []).map((item) => ({
    value: String(item.mstCompanyID),
    label: item.CompanyName ?? '',
  }))
}

export function positionOptions(data: MstPosition[] | null | undefined): SelectOption[] {
  return (data ?? []).map((item) => ({
    value: String(item.mstPositionID),
    label: item.PositionName ?? '',
  }))
}
